import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { isAdmin, isUser } from '../types';
import ProfileModal from './ProfileModal';

interface UserProfileProps {
  className?: string;
  onAction?: () => void; // Callback for when an action is taken (for mobile menu)
}

const UserProfile: React.FC<UserProfileProps> = ({ className = '', onAction }) => {
  const { user, userType, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      setIsDropdownOpen(false);
      onAction?.(); // Close mobile menu if applicable
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getUserDisplayName = (): string => {
    if (!user) return 'User';
    
    // Admin 타입 체크
    if (isAdmin(user)) {
      return user.name || user.email.split('@')[0];
    }
    
    // User 타입 (Brand/Manufacturer) 체크
    if (isUser(user)) {
      return user.contact_person || user.email.split('@')[0];
    }
    
    // fallback - email이 반드시 존재한다고 가정
    return (user as any).email?.split('@')[0] || 'User';
  };

  const getUserTypeDisplayName = (): string => {
    switch (userType) {
      case 'admin':
        // Show actual role (admin/manager) instead of generic "Administrator"
        if (isAdmin(user)) {
          return user.role || 'admin';
        }
        return 'admin';
      case 'brand':
        return 'Brand User';
      default:
        return 'User';
    }
  };

  const getInitials = (): string => {
    const name = getUserDisplayName();
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserTypeColor = (): string => {
    switch (userType) {
      case 'admin':
        return 'bg-red-500';
      case 'brand':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };


  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* User Profile Button */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
      >
        {/* User Info */}
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium text-gray-900">
            {getUserDisplayName()}
          </div>
          <div className="text-xs text-gray-500">
            {getUserTypeDisplayName()}
          </div>
        </div>
        
        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
            isDropdownOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {getUserDisplayName()}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.email}
                </div>
                <div className="text-xs text-gray-400">
                  {getUserTypeDisplayName()}
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsDropdownOpen(false);
                setShowProfileModal(true);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Profile
            </button>

            <div className="border-t border-gray-100 my-1"></div>

            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </div>
  );
};

export default UserProfile;